import { Pipe, type PipeTransform } from '@angular/core';
import { createMetadataKey, metadata, PathKind, StandardSchemaValidationError, type SchemaPath, type SchemaPathRules, type ValidationError } from '@angular/forms/signals';
import type { z } from 'zod';

/**
 * Binds the message mapper for a field, alongside its validators:
 *
 * ```ts
 * form(value$, p => {
 *   required(p.username);
 *   validateStandardSchema(p.username, username);
 *   errorMessages(p.username, usernameError);
 * });
 * ```
 *
 * The template then needs no argument: `{{ error | errorMessage }}`.
 */
export function zodErrorMessages<TValue, TPathKind extends PathKind = PathKind.Root>(
  path: SchemaPath<TValue, SchemaPathRules.Supported, TPathKind>,
  factory: ErrorMessageFn
) {
  metadata(path, ZOD_SCHEMA_ERROR_MESSAGE_FACTORY, () => factory);
}

@Pipe({ name: 'errorMessage', pure: true })
export class ErrorMessagePipe implements PipeTransform {
  transform(error: ValidationError.WithFieldTree) {
    if (error instanceof StandardSchemaValidationError) {
      const message = getStandardSchemaMessage(error);
      if (message) return message;
    }

    return error.message;
  }
}

/**
 * Identifies a validation error to `ErrorMessageFn`.
 *
 * For a schema error this is the zod issue code (`too_small`, `invalid_format`,
 * `custom`, ...); for every other error it is the error's `kind` (`required`,
 * `email`, `exists`, ...). `rule` is the `params.rule` discriminator, present
 * only on `custom` issues.
 */
function describe(error: StandardSchemaValidationError): [code: string, rule: string | undefined] {
  // `StandardSchemaV1.Issue` is vendor-agnostic and declares only `message` and
  // `path`, so widening to zod's issue union is unavoidable. The cast is
  // contained here and guarded - a non-zod schema simply yields no rule.
  const issue = error.issue as z.core.$ZodIssue;
  const rule = issue.code === 'custom' && typeof issue.params?.['rule'] === 'string' ?
    issue.params['rule'] :
    undefined;

  return [issue.code, rule];
}

/**
 * Maps a validation error to the text shown to the user.
 *
 * Receives the error's `code` and, for schema refinements, the `params.rule`
 * discriminator. Return `undefined` to fall back to `error.message`.
 *
 * Must be a pure function with a stable identity - the pipe is pure, so it only
 * re-runs when the error or the mapper reference changes. Define it at module
 * scope, not as an inline arrow in the template.
 */
export type ErrorMessageFn =
  (code: string, rule: string | undefined) => string | undefined;

const ZOD_SCHEMA_ERROR_MESSAGE_FACTORY = createMetadataKey<ErrorMessageFn>();



function getStandardSchemaMessage(error: StandardSchemaValidationError) {
  const factory = error.fieldTree().metadata(ZOD_SCHEMA_ERROR_MESSAGE_FACTORY)?.();
  if (!factory)
    return;

  return factory(...describe(error));
}
